import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSEO } from '../lib/useSEO';
import { boardsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Trophy, Users, Swords, Target, TrendingUp, Crown, Medal, BarChart3 } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

function StatCard({ icon: Icon, label, value, color = 'text-white' }) {
  return (
    <div className="bg-[hsl(var(--brand-blue-800))] border border-[hsl(var(--border))] rounded-lg p-4 text-center">
      <Icon className={`w-5 h-5 mx-auto mb-2 ${color}`} />
      <p className={`text-xl font-bold ${color}`}>{value}</p>
      <p className="text-gray-500 text-xs mt-1">{label}</p>
    </div>
  );
}

function BoardRow({ board, isCreator }) {
  const won = board.won;
  return (
    <div className="flex items-center justify-between py-3 border-b border-[hsl(var(--border))] last:border-0" data-testid={`board-stat-${board.board_id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-white text-sm font-medium truncate">{board.title}</p>
        <p className="text-gray-500 text-xs capitalize">{board.difficulty}</p>
      </div>
      {isCreator ? (
        <div className="flex items-center gap-4 shrink-0 text-sm">
          <div className="text-center">
            <p className="text-gray-400 text-xs">Plays</p>
            <p className="text-white font-bold">{board.times_played}</p>
          </div>
          <div className="text-center">
            <p className="text-gray-400 text-xs">Your Score</p>
            <p className="text-[hsl(var(--brand-gold-500))] font-bold">${board.creator_score?.toLocaleString()}</p>
          </div>
          <div className="text-center">
            <p className="text-gray-400 text-xs">Avg Score</p>
            <p className="text-gray-300 font-bold">${board.avg_challenger_score?.toLocaleString()}</p>
          </div>
          <div className="text-center">
            <p className="text-gray-400 text-xs">Beaten</p>
            <p className="text-emerald-400 font-bold">{board.challengers_beaten}/{board.times_played}</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-4 shrink-0 text-sm">
          <div className="text-center">
            <p className="text-gray-400 text-xs">Your Score</p>
            <p className={`font-bold ${won ? 'text-emerald-400' : 'text-rose-400'}`}>${board.my_score?.toLocaleString()}</p>
          </div>
          <div className="text-center">
            <p className="text-gray-400 text-xs">Creator</p>
            <p className="text-gray-300 font-bold">${board.creator_score?.toLocaleString()}</p>
          </div>
          <div className="w-16 text-center">
            {won ? (
              <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full">WON</span>
            ) : (
              <span className="text-xs font-bold text-rose-400 bg-rose-500/10 px-2 py-1 rounded-full">LOST</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function BoardStats() {
  useSEO('Board Statistics | JepPrep', 'View your board creation and community challenge statistics on JepPrep.');
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('creator');

  useEffect(() => {
    boardsAPI.stats().then(r => { setStats(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[hsl(var(--brand-gold-500))] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const creator = stats?.creator || {};
  const challenger = stats?.challenger || {};

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 items-center gap-4">
            <Button variant="ghost" size="sm" className="text-gray-300" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <img src={LOGO_URL} alt="JepPrep" className="h-10" />
          </div>
        </div>
      </nav>

      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <BarChart3 className="w-6 h-6 text-[hsl(var(--brand-gold-500))]" />
          <h1 className="text-2xl font-bold text-white" data-testid="board-stats-title">Board Statistics</h1>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 mb-6" data-testid="stats-tabs">
          <Button
            variant={tab === 'creator' ? 'default' : 'ghost'}
            className={tab === 'creator' ? 'bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))]' : 'text-gray-400'}
            onClick={() => setTab('creator')}
            data-testid="tab-creator"
          >
            <Crown className="w-4 h-4 mr-2" />
            My Boards
          </Button>
          <Button
            variant={tab === 'challenger' ? 'default' : 'ghost'}
            className={tab === 'challenger' ? 'bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))]' : 'text-gray-400'}
            onClick={() => setTab('challenger')}
            data-testid="tab-challenger"
          >
            <Swords className="w-4 h-4 mr-2" />
            Challenges
          </Button>
        </div>

        {tab === 'creator' ? (
          <>
            {/* Creator Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <StatCard icon={Trophy} label="Boards Posted" value={creator.boards_posted || 0} color="text-[hsl(var(--brand-gold-500))]" />
              <StatCard icon={Users} label="Total Plays" value={creator.total_plays || 0} color="text-blue-400" />
              <StatCard icon={Target} label="Challengers Beaten" value={creator.challengers_beaten || 0} color="text-emerald-400" />
              <StatCard icon={TrendingUp} label="Beat Rate" value={creator.total_challengers > 0 ? `${Math.round((creator.challengers_beaten / creator.total_challengers) * 100)}%` : '—'} color="text-violet-400" />
            </div>

            {/* Creator Board List */}
            <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <CardContent className="p-4">
                {creator.boards?.length > 0 ? (
                  creator.boards.map(b => <BoardRow key={b.board_id} board={b} isCreator />)
                ) : (
                  <div className="text-center py-8">
                    <Trophy className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 mb-2">No boards posted to Community yet</p>
                    <Link to="/boards/new">
                      <Button size="sm" className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))]">Create a Board</Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            {/* Challenger Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <StatCard icon={Swords} label="Boards Challenged" value={challenger.boards_played || 0} color="text-[hsl(var(--brand-gold-500))]" />
              <StatCard icon={Medal} label="Wins" value={challenger.wins || 0} color="text-emerald-400" />
              <StatCard icon={Target} label="Win Rate" value={challenger.boards_played > 0 ? `${Math.round((challenger.wins / challenger.boards_played) * 100)}%` : '—'} color="text-violet-400" />
              <StatCard icon={TrendingUp} label="Best Score" value={challenger.best_score > 0 ? `$${challenger.best_score.toLocaleString()}` : '—'} color="text-blue-400" />
            </div>

            {/* Challenger Board List */}
            <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <CardContent className="p-4">
                {challenger.boards?.length > 0 ? (
                  challenger.boards.map(b => <BoardRow key={b.board_id} board={b} isCreator={false} />)
                ) : (
                  <div className="text-center py-8">
                    <Swords className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 mb-2">No community boards challenged yet</p>
                    <Link to="/community">
                      <Button size="sm" className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))]">Browse Community</Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
